import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Play, Pause, Calendar, Clock, Users, Building, DollarSign, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useHubSpotSync, useHubSpotSyncHistory } from '@/hooks/useHubSpot';
import { formatDistanceToNow } from '@/lib/format';

interface EntitySyncOption {
  id: string;
  label: string;
  icon: any;
  enabled: boolean;
}

export default function HubSpotSync() {
  const navigate = useNavigate();
  const { mutate: startSync, isPending: syncInProgress } = useHubSpotSync();
  const { data: syncHistory } = useHubSpotSyncHistory();

  const [selectedEntities, setSelectedEntities] = useState<EntitySyncOption[]>([
    { id: 'contact', label: 'Contacts', icon: Users, enabled: true },
    { id: 'company', label: 'Companies', icon: Building, enabled: true },
    { id: 'deal', label: 'Deals', icon: DollarSign, enabled: true },
    { id: 'engagement', label: 'Engagements', icon: Activity, enabled: false },
  ]);

  const [currentSync, setCurrentSync] = useState<any>(null);

  const handleStartSync = () => {
    const enabledEntities = selectedEntities.filter(e => e.enabled);
    
    if (enabledEntities.length === 0) {
      return;
    }

    // If all entities are selected, sync all
    const entityType = enabledEntities.length === selectedEntities.length 
      ? 'all' 
      : enabledEntities[0].id as any;

    startSync({ 
      entityType, 
      syncType: 'manual',
      direction: 'hubspot_to_crm'
    });
  };

  const toggleEntity = (entityId: string) => {
    setSelectedEntities(prev => 
      prev.map(entity => 
        entity.id === entityId 
          ? { ...entity, enabled: !entity.enabled }
          : entity
      )
    );
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      pending: 'secondary',
      running: 'default',
      completed: 'success',
      failed: 'destructive',
    };

    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const activeSyncs = syncHistory?.filter(h => h.status === 'running') || [];
  const recentSyncs = syncHistory?.filter(h => h.status !== 'running').slice(0, 5) || [];

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/integrations/hubspot')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Sync Management</h1>
          <p className="text-muted-foreground mt-1">
            Configure and monitor HubSpot data synchronization
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Sync Control */}
        <Card>
          <CardHeader>
            <CardTitle>Sync Control</CardTitle>
            <CardDescription>
              Select entities to sync from HubSpot
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {selectedEntities.map((entity) => (
                <div key={entity.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={entity.id}
                    checked={entity.enabled}
                    onCheckedChange={() => toggleEntity(entity.id)}
                    disabled={syncInProgress}
                  />
                  <Label
                    htmlFor={entity.id}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <entity.icon className="h-4 w-4" />
                    {entity.label}
                  </Label>
                </div>
              ))}
            </div>

            <div className="pt-4">
              <Button
                className="w-full"
                onClick={handleStartSync}
                disabled={syncInProgress || !selectedEntities.some(e => e.enabled)}
              >
                {syncInProgress ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Start Sync
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active Syncs */}
        <Card>
          <CardHeader>
            <CardTitle>Active Syncs</CardTitle>
            <CardDescription>
              Currently running synchronization tasks
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeSyncs.length > 0 ? (
              <div className="space-y-3">
                {activeSyncs.map((sync) => (
                  <div key={sync.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {sync.entity_type} sync
                      </span>
                      {getStatusBadge(sync.status)}
                    </div>
                    <Progress value={sync.metadata?.progress || 0} />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{sync.records_processed} processed</span>
                      <span>{sync.metadata?.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active syncs</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Sync History */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Sync History</CardTitle>
          <CardDescription>
            View past synchronization operations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentSyncs.length > 0 ? (
            <div className="space-y-3">
              {recentSyncs.map((sync) => (
                <div key={sync.id} className="flex items-center justify-between py-3 border-b last:border-0">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium capitalize">
                        {sync.entity_type} sync
                      </span>
                      {getStatusBadge(sync.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <span>{formatDistanceToNow(new Date(sync.started_at))} ago</span>
                      {sync.completed_at && (
                        <span> • Duration: {Math.round((new Date(sync.completed_at).getTime() - new Date(sync.started_at).getTime()) / 1000)}s</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">
                      <span className="text-green-600">{sync.records_created} created</span>
                      <span className="text-muted-foreground"> • </span>
                      <span className="text-blue-600">{sync.records_updated} updated</span>
                      {sync.records_failed > 0 && (
                        <>
                          <span className="text-muted-foreground"> • </span>
                          <span className="text-red-600">{sync.records_failed} failed</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No sync history available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}